import { Canonical } from '../hashState';
import { Rng, RngState } from '../Rng';
import { TerrainGrid, CellState } from './TerrainGrid';

/**
 * Individual fruit. Ripeness and size decide what a forager gets for it.
 */
interface Fruit {
  hue: number;        // Color hue (red-orange-yellow range)
  size: number;       // Size variation (0.8-1.2)
  age: number;        // How long this fruit has existed (ms of sim-time)
  maxAge: number;     // When it will decay (ms of sim-time)
}

/**
 * Represents a food source on the surface (typically a bush).
 */
export interface FoodSource {
  id: number;
  gridX: number;
  gridY: number;
  fruits: Fruit[];    // Individual fruits
  maxAmount: number;  // Max fruits this source can hold
  regenTimer: number; // Ms until next fruit spawns
  regenRate: number;  // Ms between fruit spawns
  variationIndex: number; // Which sprite variation the renderer draws
}

/** How many sprite variations a renderer may pick between per fruit count. */
export const FRUIT_VARIATIONS_PER_COUNT = 25;

/** Every bush and every fruit on it. See `FoodSourceManager.save()`. */
export interface FoodSourceSnapshot {
  sources: FoodSource[];
  nextId: number;
  rng: RngState;
}

/**
 * Manages food sources on the parkington surface.
 * Bushes act as food sources that regenerate over time.
 *
 * Simulation only: the pre-rendered fruit sprites live in
 * `rendering/FoodSourceRenderer.ts`, because building them calls
 * `document.createElement` and this class has to construct in Node.
 */
export interface FoodSourceConfig {
  MAX_FRUITS: number;
  REGEN_RATE_MIN: number;
  REGEN_RATE_MAX: number;
  FRUIT_LIFESPAN_MIN: number;
  FRUIT_LIFESPAN_MAX: number;
}

export const DEFAULT_FOOD_SOURCE_CONFIG: FoodSourceConfig = {
  MAX_FRUITS: 6,
  REGEN_RATE_MIN: 6000,
  REGEN_RATE_MAX: 18000,
  FRUIT_LIFESPAN_MIN: 20000,
  FRUIT_LIFESPAN_MAX: 60000,
};

export class FoodSourceManager {
  private sources: Map<number, FoodSource> = new Map();
  private nextId = 0;
  private grid: TerrainGrid;
  private rng: Rng;
  private fsConfig: FoodSourceConfig;

  constructor(grid: TerrainGrid, rng: Rng, config: Partial<FoodSourceConfig> = {}) {
    this.grid = grid;
    this.rng = rng;
    this.fsConfig = { ...DEFAULT_FOOD_SOURCE_CONFIG, ...config };
    this.initializeSources();
  }

  /**
   * Scan the grid for natural cells with bushes and create food sources.
   */
  private initializeSources(): void {
    const cols = this.grid.getCols();
    const rows = this.grid.getRows();

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = this.grid.getCell(x, y);
        if (cell && cell.state === CellState.NATURAL && cell.vegetationType === 'bush') {
          this.createFoodSource(x, y);
        }
      }
    }
  }

  /**
   * Create a random fruit with position and color variation.
   */
  private createFruit(): Fruit {
    return {
      hue: this.rng.range(0, 40), // Red (0) to orange-yellow (40)
      size: this.rng.range(0.8, 1.2), // Size variation
      age: 0,
      maxAge: this.rng.range(this.fsConfig.FRUIT_LIFESPAN_MIN, this.fsConfig.FRUIT_LIFESPAN_MAX),
    };
  }

  /**
   * Create a new food source at the given grid position.
   */
  private createFoodSource(gridX: number, gridY: number): FoodSource {
    const initialFruits = 2 + this.rng.int(4); // Start with 2-5 fruits
    const fruits: Fruit[] = [];

    for (let i = 0; i < initialFruits; i++) {
      fruits.push(this.createFruit());
    }

    const id = this.nextId++;
    const source: FoodSource = {
      id,
      gridX,
      gridY,
      fruits,
      maxAmount: this.fsConfig.MAX_FRUITS,
      regenTimer: this.rng.range(this.fsConfig.REGEN_RATE_MIN, this.fsConfig.REGEN_RATE_MAX),
      regenRate: this.rng.range(this.fsConfig.REGEN_RATE_MIN, this.fsConfig.REGEN_RATE_MAX),
      variationIndex: id % FRUIT_VARIATIONS_PER_COUNT, // Deterministic variation based on ID
    };

    this.sources.set(source.id, source);
    return source;
  }

  /**
   * Put a food source on a cell whose bush has just come in.
   *
   * The only way a source is created after construction, and it exists because
   * `initializeSources()` scans the grid once: before ADR-020 the meadow a district
   * was born with was the only meadow it would ever have, and `initializeSources`
   * ran before anything could have planted anything.
   *
   * Refused unless the cell is natural ground with a bush on it — the same
   * condition `initializeSources` requires and `update` enforces every tick — and
   * refused where a source already stands, so two callers cannot double a bush.
   *
   * The caller is `SurfaceSim`, after `TerrainGrid.plantBush`. Two steps rather than
   * one because vegetation is the ground's and fruit is this class's, and neither
   * can reach the other.
   */
  plantAt(gridX: number, gridY: number): boolean {
    const cell = this.grid.getCell(gridX, gridY);
    if (!cell || cell.state !== CellState.NATURAL || cell.vegetationType !== 'bush') {
      return false;
    }
    if (this.getSourceAt(gridX, gridY) !== null) return false;

    this.createFoodSource(gridX, gridY);
    return true;
  }

  /**
   * Update food sources - regenerate and decay food over time.
   */
  update(dt: number): void {
    for (const source of this.sources.values()) {
      // Check if the cell is still natural with a bush
      const cell = this.grid.getCell(source.gridX, source.gridY);
      if (!cell || cell.state !== CellState.NATURAL || cell.vegetationType !== 'bush') {
        // Bush was destroyed by development
        this.sources.delete(source.id);
        continue;
      }

      // Age existing fruits and remove decayed ones
      for (let i = source.fruits.length - 1; i >= 0; i--) {
        source.fruits[i].age += dt;
        if (source.fruits[i].age >= source.fruits[i].maxAge) {
          source.fruits.splice(i, 1); // Fruit decayed
        }
      }

      // Regenerate food. The timer carries its overshoot rather than being reset
      // to the full interval, so the regeneration period stays exact at any
      // cadence (ADR-002 §3).
      if (source.fruits.length < source.maxAmount) {
        source.regenTimer -= dt;
        if (source.regenTimer <= 0) {
          source.fruits.push(this.createFruit());
          source.regenTimer += source.regenRate;
          if (source.regenTimer <= 0) source.regenTimer = source.regenRate;
        }
      }
    }
  }

  /**
   * Try to collect food from a specific grid position.
   * Returns { amount, hue } where amount is food units and hue is the fruit color (0-40).
   * Each fruit is worth 5-15 food units (varies by size/ripeness).
   */
  collectFood(gridX: number, gridY: number): { amount: number; hue: number } {
    for (const source of this.sources.values()) {
      if (source.gridX === gridX && source.gridY === gridY && source.fruits.length > 0) {
        const fruit = source.fruits.pop()!; // Remove one fruit
        // Larger, older fruits are worth more (5-15 units)
        const baseValue = 5;
        const sizeBonus = Math.floor(fruit.size * 5);
        const ageBonus = Math.floor((fruit.age / fruit.maxAge) * 5);
        return { amount: baseValue + sizeBonus + ageBonus, hue: fruit.hue };
      }
    }
    return { amount: 0, hue: 0 };
  }

  /**
   * Find the nearest food source with available food.
   * Returns null if no food available.
   */
  getNearestFood(gridX: number, gridY: number): FoodSource | null {
    let nearest: FoodSource | null = null;
    let nearestDist = Infinity;

    for (const source of this.sources.values()) {
      if (source.fruits.length <= 0) continue;

      const dist = Math.abs(source.gridX - gridX) + Math.abs(source.gridY - gridY);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = source;
      }
    }

    return nearest;
  }

  /**
   * Get all food sources.
   */
  getAllSources(): FoodSource[] {
    return Array.from(this.sources.values());
  }

  /**
   * Get food source at a specific grid position.
   */
  getSourceAt(gridX: number, gridY: number): FoodSource | null {
    for (const source of this.sources.values()) {
      if (source.gridX === gridX && source.gridY === gridY) {
        return source;
      }
    }
    return null;
  }

  /**
   * Check if a cell has food available.
   */
  hasFoodAt(gridX: number, gridY: number): boolean {
    const source = this.getSourceAt(gridX, gridY);
    return source !== null && source.fruits.length > 0;
  }

  /**
   * Get source data for pheromone food odor computation.
   */
  getAllSourceData(): Array<{ x: number; y: number; fruitCount: number; maxFruits: number }> {
    const result: Array<{ x: number; y: number; fruitCount: number; maxFruits: number }> = [];
    for (const source of this.sources.values()) {
      result.push({
        x: source.gridX,
        y: source.gridY,
        fruitCount: source.fruits.length,
        maxFruits: source.maxAmount,
      });
    }
    return result;
  }

  getConfig(): FoodSourceConfig {
    return { ...this.fsConfig };
  }

  updateConfig(partial: Partial<FoodSourceConfig>): void {
    this.fsConfig = { ...this.fsConfig, ...partial };
  }

  /**
   * Get total food available across all sources.
   */
  getTotalFood(): number {
    let total = 0;
    for (const source of this.sources.values()) {
      total += source.fruits.length;
    }
    return total;
  }

  /**
   * Every bush's complete state.
   *
   * In `Map` order, which a snapshot preserves rather than canonicalises — see
   * `core/snapshot.ts`. Here that order happens to be ascending id, since sources
   * are created in one grid scan and only ever removed, but relying on that would
   * be relying on something nothing promises.
   *
   * `variationIndex` is recomputed on load rather than stored: it is a function of
   * the id, and it is what the renderer draws rather than anything the bush does.
   */
  save(): FoodSourceSnapshot {
    const sources = Array.from(this.sources.values())
      .map((s) => ({ ...s, fruits: s.fruits.map((f) => ({ ...f })) }));
    return { sources, nextId: this.nextId, rng: this.rng.save() };
  }

  /** Restore from `save()`. */
  load(state: FoodSourceSnapshot): void {
    this.sources.clear();
    for (const s of state.sources) {
      this.sources.set(s.id, {
        ...s,
        fruits: s.fruits.map((f) => ({ ...f })),
        variationIndex: s.id % FRUIT_VARIATIONS_PER_COUNT,
      });
    }
    this.nextId = state.nextId;
    this.rng.load(state.rng);
  }

  /**
   * Order-stable view of every bush's state, for hashing.
   *
   * Sorted by id rather than taken in Map order: a restored world inserts its
   * sources in a different sequence than a fresh one built them.
   */
  canonicalState(): Canonical {
    return Array.from(this.sources.values())
      .sort((a, b) => a.id - b.id)
      .map((s) => ({
        id: s.id,
        x: s.gridX,
        y: s.gridY,
        regenTimer: s.regenTimer,
        regenRate: s.regenRate,
        fruits: s.fruits.map((f) => ({
          hue: f.hue, size: f.size, age: f.age, maxAge: f.maxAge,
        })),
      }));
  }
}
